import {
  DEFAULT_SELECTOR_TYPE,
  type SelectorConfig,
  type SelectorDescriptor,
  type SelectorInput,
  type SelectorType,
} from '@/config/shared/selector';
import type { PixelLogger } from '@/logger';

import { isTextMatchLeaf, queryElementsByText } from './TextSelectorResolver';

const PREFIX_TO_TYPE: Record<string, SelectorType> = {
  css: 'css',
  id: 'id',
  class: 'class',
  attr: 'attribute',
  attribute: 'attribute',
  text: 'text',
};

export type NormalizedSelector = {
  readonly type: SelectorType;
  readonly value: string;
  readonly exact?: boolean;
  readonly caseSensitive?: boolean;
  readonly closest?: string;
};

type SelectorLogger = Pick<PixelLogger, 'debug'>;

/**
 * Resolve `SelectorConfig` (string | array | objeto) em descritores normalizados
 * e consulta/match no DOM de forma SSR-safe.
 *
 * Ordem de resolução por item:
 * 1. Objeto → type do objeto
 * 2. String com prefixo (css:|id:|class:|attr:|attribute:|text:) → prefixo
 * 3. String sem prefixo → selectorType (fallback)
 * 4. Sem selectorType → css
 */
export class SelectorResolver {
  private readonly logger: SelectorLogger | null;

  constructor(logger?: SelectorLogger | null) {
    this.logger = logger ?? null;
  }

  hasEntries(selector: SelectorConfig | undefined): boolean {
    return this.normalize(selector).length > 0;
  }

  normalize(
    selector: SelectorConfig | undefined,
    selectorType: SelectorType = DEFAULT_SELECTOR_TYPE
  ): NormalizedSelector[] {
    if (selector == null) return [];

    const inputs = Array.isArray(selector) ? selector : [selector];
    const result: NormalizedSelector[] = [];

    for (const input of inputs) {
      const normalized = this.normalizeInput(input, selectorType);
      if (normalized) result.push(normalized);
    }

    return result;
  }

  /**
   * Retorna elementos que batem com qualquer seletor (OR).
   * Selector inválido → debug log + lista vazia para aquele item.
   */
  queryAll(
    selector: SelectorConfig | undefined,
    selectorType: SelectorType = DEFAULT_SELECTOR_TYPE,
    root?: ParentNode | null
  ): Element[] {
    if (typeof document === 'undefined') return [];

    const scope = root ?? document;
    const matched: Element[] = [];

    for (const item of this.normalize(selector, selectorType)) {
      matched.push(...this.queryNormalized(item, scope));
    }

    return Array.from(new Set(matched));
  }

  /**
   * Verifica se `element` satisfaz algum seletor normalizado.
   * Usado no walk-up de click — não usa `element.matches('text:...')`.
   */
  matches(
    element: Element,
    selector: SelectorConfig | undefined,
    selectorType: SelectorType = DEFAULT_SELECTOR_TYPE
  ): boolean {
    for (const item of this.normalize(selector, selectorType)) {
      if (this.matchesNormalized(element, item)) return true;
    }
    return false;
  }

  private normalizeInput(
    input: SelectorInput,
    fallbackType: SelectorType
  ): NormalizedSelector | null {
    if (typeof input !== 'string') {
      return this.fromDescriptor(input);
    }

    const trimmed = input.trim();
    if (!trimmed) return null;

    const prefixed = this.parsePrefix(trimmed);
    if (prefixed) {
      return this.fromDescriptor({
        type: prefixed.type,
        value: prefixed.value,
      });
    }

    return this.fromDescriptor({
      type: fallbackType,
      value: trimmed,
    });
  }

  private fromDescriptor(
    descriptor: SelectorDescriptor
  ): NormalizedSelector | null {
    const value = descriptor.value?.trim();
    if (!value) return null;

    return {
      type: descriptor.type,
      value,
      exact: descriptor.exact,
      caseSensitive: descriptor.caseSensitive,
      closest: descriptor.closest,
    };
  }

  private parsePrefix(
    raw: string
  ): { type: SelectorType; value: string } | null {
    const colon = raw.indexOf(':');
    if (colon <= 0) return null;

    const prefix = raw.slice(0, colon).toLowerCase();
    const type = PREFIX_TO_TYPE[prefix];
    if (!type) return null;

    const value = raw.slice(colon + 1).trim();
    if (!value) return null;

    return { type, value };
  }

  private queryNormalized(
    item: NormalizedSelector,
    root: ParentNode
  ): Element[] {
    if (item.type === 'text') {
      return queryElementsByText(
        {
          value: item.value,
          exact: item.exact,
          caseSensitive: item.caseSensitive,
          closest: item.closest,
        },
        root
      );
    }

    const css = this.toCssSelector(item);
    if (!css) return [];

    try {
      const nodes = Array.from(root.querySelectorAll(css));
      if (!item.closest) return nodes;

      return nodes.filter(el => {
        try {
          return el.closest(item.closest!) != null;
        } catch {
          return false;
        }
      });
    } catch (error) {
      this.logger?.debug(
        `[SelectorResolver] Invalid CSS selector ignored: ${css}`,
        error
      );
      return [];
    }
  }

  private matchesNormalized(
    element: Element,
    item: NormalizedSelector
  ): boolean {
    if (item.closest) {
      try {
        if (element.closest(item.closest) == null) return false;
      } catch {
        this.logger?.debug(
          `[SelectorResolver] Invalid closest selector ignored: ${item.closest}`
        );
        return false;
      }
    }

    if (item.type === 'text') {
      // Folha apenas — senão walk-up dispara em card/body que contém o botão
      return isTextMatchLeaf(element, {
        value: item.value,
        exact: item.exact,
        caseSensitive: item.caseSensitive,
      });
    }

    const css = this.toCssSelector(item);
    if (!css) return false;

    try {
      return element.matches(css);
    } catch (error) {
      this.logger?.debug(
        `[SelectorResolver] Invalid CSS selector ignored: ${css}`,
        error
      );
      return false;
    }
  }

  /**
   * Converte descritor não-text em CSS.
   * Preserva compat legado: `#` / `.` / `[` passam direto;
   * `attribute` sem isso vira `[data-track="..."]`.
   */
  toCssSelector(
    item: Pick<NormalizedSelector, 'type' | 'value'>
  ): string | null {
    if (item.type === 'text') return null;

    const { value } = item;
    if (
      value.startsWith('[') ||
      value.startsWith('.') ||
      value.startsWith('#')
    ) {
      return value;
    }

    switch (item.type) {
      case 'id':
        return `#${escapeCssIdent(value)}`;
      case 'class':
        return `.${escapeCssIdent(value)}`;
      case 'attribute':
        return `[data-track="${escapeAttrValue(value)}"]`;
      case 'css':
        return value;
      default:
        return value;
    }
  }
}

function escapeCssIdent(value: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(value);
  }
  return value.replace(/([ !"#$%&'()*+,./:;<=>?@[\\\]^`{|}~])/g, '\\$1');
}

function escapeAttrValue(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}
